import type { WebhookDeliveryStatus } from "@prisma/client";
import { z } from "zod";
import type { PaginatedResponse } from "../common/pagination.types";

// ============================================================================
// Constants - Webhook Headers & Configuration
// ============================================================================

export const WEBHOOK_SIGNATURE_HEADER = "x-dune-webhook-signature";
export const WEBHOOK_TIMESTAMP_HEADER = "x-dune-webhook-timestamp";
export const WEBHOOK_ID_HEADER = "x-dune-webhook-id";
export const WEBHOOK_VERSION_HEADER = "x-dune-webhook-version";
export const WEBHOOK_SIGNATURE_VERSION = "v1";
export const WEBHOOK_TIMESTAMP_TOLERANCE_MS = 5 * 60 * 1000;

// ============================================================================
// Webhook Events
// ============================================================================

export const WEBHOOK_EVENT_VALUES = [
	"NEW_PROSPECT",
	"NEW_BROKER",
	"UPDATE_BROKER",
	"NEW_CONSTRUCTION_SITE",
	"UPDATE_CONSTRUCTION_SITE",
	"VALIDATE_CONSTRUCTION_SITE",
	"TO_RECEIVE_CONSTRUCTION_SITE",
	"RECEIVED_CONSTRUCTION_SITE",
	"NEW_PROPOSITION",
	"SIGNED_PROPOSITION",
	"NEW_CONTRACT",
	"SIGNED_CONTRACT",
	"NEW_RECEIPT",
	"UPDATE_RECEIPT",
	"PAID_RECEIPT",
] as const;

export type WebhookEvent = (typeof WEBHOOK_EVENT_VALUES)[number];

// ============================================================================
// Webhook Event Details - Type Helper
// ============================================================================

/**
 * Factory function pour créer un détail d'événement avec typage strict.
 * Garantit que l'exemple correspond exactement au schema Zod.
 */
function defineEvent<T extends z.ZodObject<z.ZodRawShape>>(detail: {
	label: string;
	description: string;
	schema: T;
	example: z.infer<T>;
}) {
	return detail;
}

// ============================================================================
// Webhook Event Details
// ============================================================================

export const WEBHOOK_EVENT_DETAILS = {
	// --- Prospects & Brokers ---
	NEW_PROSPECT: defineEvent({
		label: "Nouveau prospect",
		description: "Déclenché lorsqu'un nouveau prospect est ajouté dans le système Dune.",
		schema: z.object({
			event: z.literal("NEW_PROSPECT"),
			userId: z.number().describe("Identifiant unique du prospect (user) Dune."),
		}),
		example: { event: "NEW_PROSPECT", userId: 123 },
	}),

	NEW_BROKER: defineEvent({
		label: "Nouvel apporteur",
		description: "Déclenché lorsqu'un prospect devient apporteur.",
		schema: z.object({
			event: z.literal("NEW_BROKER"),
			userId: z.number().describe("Identifiant unique du broker (user) Dune."),
		}),
		example: { event: "NEW_BROKER", userId: 123 },
	}),

	UPDATE_BROKER: defineEvent({
		label: "Mise à jour apporteur",
		description: "Déclenché lorsque les informations d'un apporteur sont mis à jour.",
		schema: z.object({
			event: z.literal("UPDATE_BROKER"),
			userId: z.number().describe("Identifiant unique du broker (user) Dune."),
		}),
		example: { event: "UPDATE_BROKER", userId: 123 },
	}),

	// --- Construction Sites ---
	NEW_CONSTRUCTION_SITE: defineEvent({
		label: "Nouveau chantier",
		description: "Déclenché lorsqu'un nouveau chantier est créé.",
		schema: z.object({
			event: z.literal("NEW_CONSTRUCTION_SITE"),
			constructionSiteId: z.number().describe("Identifiant unique du chantier Dune."),
		}),
		example: { event: "NEW_CONSTRUCTION_SITE", constructionSiteId: 456 },
	}),

	UPDATE_CONSTRUCTION_SITE: defineEvent({
		label: "Mise à jour chantier",
		description: "Déclenché lorsqu'un chantier est mis à jour.",
		schema: z.object({
			event: z.literal("UPDATE_CONSTRUCTION_SITE"),
			constructionSiteId: z.number().describe("Identifiant unique du chantier Dune."),
		}),
		example: { event: "UPDATE_CONSTRUCTION_SITE", constructionSiteId: 456 },
	}),

	VALIDATE_CONSTRUCTION_SITE: defineEvent({
		label: "Chantier validé",
		description: "Déclenché lorsqu'un chantier est validé.",
		schema: z.object({
			event: z.literal("VALIDATE_CONSTRUCTION_SITE"),
			constructionSiteId: z.number().describe("Identifiant unique du chantier Dune."),
		}),
		example: { event: "VALIDATE_CONSTRUCTION_SITE", constructionSiteId: 456 },
	}),

	TO_RECEIVE_CONSTRUCTION_SITE: defineEvent({
		label: "Chantier à réceptionner",
		description: "Déclenché lorsqu'un chantier est marqué comme 'À réceptionner'.",
		schema: z.object({
			event: z.literal("TO_RECEIVE_CONSTRUCTION_SITE"),
			constructionSiteId: z.number().describe("Identifiant unique du chantier Dune."),
		}),
		example: { event: "TO_RECEIVE_CONSTRUCTION_SITE", constructionSiteId: 456 },
	}),

	RECEIVED_CONSTRUCTION_SITE: defineEvent({
		label: "Chantier réceptionné",
		description: "Déclenché lorsqu'un chantier est marqué comme 'Réceptionné'.",
		schema: z.object({
			event: z.literal("RECEIVED_CONSTRUCTION_SITE"),
			constructionSiteId: z.number().describe("Identifiant unique du chantier Dune."),
		}),
		example: { event: "RECEIVED_CONSTRUCTION_SITE", constructionSiteId: 456 },
	}),

	// --- Propositions ---
	NEW_PROPOSITION: defineEvent({
		label: "Nouvelle proposition",
		description: "Déclenché lorsqu'une nouvelle proposition d'assurance est créée.",
		schema: z.object({
			event: z.literal("NEW_PROPOSITION"),
			propositionId: z.number().describe("Identifiant unique de la proposition Dune."),
		}),
		example: { event: "NEW_PROPOSITION", propositionId: 789 },
	}),

	SIGNED_PROPOSITION: defineEvent({
		label: "Proposition signée",
		description: "Déclenché lorsqu'une proposition d'assurance est signée.",
		schema: z.object({
			event: z.literal("SIGNED_PROPOSITION"),
			propositionId: z.number().describe("Identifiant unique de la proposition Dune."),
		}),
		example: { event: "SIGNED_PROPOSITION", propositionId: 789 },
	}),

	// --- Contracts ---
	NEW_CONTRACT: defineEvent({
		label: "Nouveau contrat",
		description: "Déclenché lorsqu'un nouveau contrat d'assurance est créé.",
		schema: z.object({
			event: z.literal("NEW_CONTRACT"),
			contractId: z.number().describe("Identifiant unique du contrat Dune."),
		}),
		example: { event: "NEW_CONTRACT", contractId: 1011 },
	}),

	SIGNED_CONTRACT: defineEvent({
		label: "Contrat signé",
		description: "Déclenché lorsqu'un contrat d'assurance est signé.",
		schema: z.object({
			event: z.literal("SIGNED_CONTRACT"),
			contractId: z.number().describe("Identifiant unique du contrat Dune."),
		}),
		example: { event: "SIGNED_CONTRACT", contractId: 1011 },
	}),

	// --- Receipts ---
	NEW_RECEIPT: defineEvent({
		label: "Nouvelle quittance",
		description: "Déclenché lorsqu'une nouvelle quittance est générée.",
		schema: z.object({
			event: z.literal("NEW_RECEIPT"),
			receiptId: z.number().describe("Identifiant unique de la quittance Dune."),
		}),
		example: { event: "NEW_RECEIPT", receiptId: 1213 },
	}),

	UPDATE_RECEIPT: defineEvent({
		label: "Mise à jour quittance",
		description: "Déclenché lorsqu'une quittance est modifiée.",
		schema: z.object({
			event: z.literal("UPDATE_RECEIPT"),
			receiptId: z.number().describe("Identifiant unique de la quittance Dune."),
		}),
		example: { event: "UPDATE_RECEIPT", receiptId: 1213 },
	}),

	PAID_RECEIPT: defineEvent({
		label: "Quittance payée",
		description: "Déclenché lorsqu'une quittance est marquée comme payée.",
		schema: z.object({
			event: z.literal("PAID_RECEIPT"),
			receiptId: z.number().describe("Identifiant unique de la quittance Dune."),
		}),
		example: { event: "PAID_RECEIPT", receiptId: 1213 },
	}),
} as const;

// ============================================================================
// Webhook Payload Types
// ============================================================================

export type WebhookPayloadMap = {
	[E in WebhookEvent]: z.infer<(typeof WEBHOOK_EVENT_DETAILS)[E]["schema"]>;
};

export type WebhookPayload<E extends WebhookEvent> = WebhookPayloadMap[E];

// ============================================================================
// Webhook DTOs
// ============================================================================

export interface WebhookDto {
	id: number;
	name: string;
	url: string;
	description?: string | null;
	isActive: boolean;
	events: WebhookEvent[];
	createdAt: string;
	updatedAt: string;
	createdById: number;
	updatedById: number | null;
	lastDeliveryAt?: string | null;
	lastResponseStatusCode?: number | null;
}

export interface WebhookWithSecretDto extends WebhookDto {
	secret: string;
}

export interface CreateWebhookResult {
	secret: string;
	webhook: WebhookDto;
}

export interface RotateWebhookSecretResult {
	secret: string;
}

// ============================================================================
// Webhook Delivery DTOs
// ============================================================================

export interface WebhookDeliveryDto {
	id: number;
	webhookId: number;
	webhook?: {
		id: number;
		name: string;
		url: string;
	};
	event: WebhookEvent;
	payload: WebhookPayload<WebhookEvent>;
	signature: string;
	timestamp: string | null;
	status: WebhookDeliveryStatus;
	responseStatusCode: number | null;
	responseError: string | null;
	responseBody: string | null;
	attemptCount: number;
	lastAttemptAt: string | null;
	nextAttemptAt: string | null;
}

export type WebhookDeliveryList = PaginatedResponse<WebhookDeliveryDto>;

// ============================================================================
// Webhook Test Payloads
// ============================================================================

export interface TriggerWebhookTestPayload {
	event: WebhookEvent;
}

export interface TriggerWebhookTestResult {
	deliveriesCount: number;
}

